import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Search, X } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminAccessDenied } from "@/components/admin/AdminAccessDenied";
import { AdminLoading, useAdminAuth } from "@/components/admin/useAdminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const APPLICATION_STATUSES = ["submitted", "under_review", "shortlisted", "interview", "selected", "offer", "documentation", "visa_processing", "placement", "rejected", "withdrawn", "new", "hired"];

const STATUS_STYLES = {
    submitted: "border-forest/40 text-forest",
    new: "border-forest/40 text-forest",
    under_review: "border-ink/30 text-ink/60",
    shortlisted: "border-ink/30 text-ink/60",
    interview: "border-champagne/60 text-[#8a7320]",
    selected: "border-forest/60 text-forest",
    offer: "border-forest/60 text-forest",
    documentation: "border-ink/30 text-ink/60",
    visa_processing: "border-ink/30 text-ink/60",
    placement: "border-forest bg-forest text-ivory",
    rejected: "border-ink/20 text-ink/40",
    hired: "border-forest bg-forest text-ivory",
    withdrawn: "border-ink/15 text-ink/35",
};

const CANDIDATE_FIELDS = [
    ["full_name", "Full Name"],
    ["email", "Email"],
    ["phone", "Phone / WhatsApp"],
    ["country_of_residence", "Country of Residence"],
    ["nationality", "Nationality"],
    ["current_location", "Current Location"],
    ["profession", "Profession"],
    ["years_experience", "Years of Experience"],
    ["highest_qualification", "Highest Qualification"],
    ["certifications", "Certifications"],
    ["preferred_destination", "Preferred Destination"],
];

const formatDate = (iso) =>
    new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminApplications() {
    const { user, logout, hasPermission } = useAdminAuth();
    const [jobs, setJobs] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ job_id: "", status: "", q: "" });
    const [selected, setSelected] = useState(null);
    const [note, setNote] = useState("");

    useEffect(() => {
        if (!user) return;
        fetch(`${API}/api/admin/jobs`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : []))
            .then(setJobs)
            .catch(() => {});
    }, [user]);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            const res = await fetch(`${API}/api/admin/applications?${params}`, { credentials: "include" });
            if (res.ok) setItems(await res.json());
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        if (!user) return;
        const t = setTimeout(fetchList, 300);
        return () => clearTimeout(t);
    }, [user, fetchList]);

    const openDetail = async (id) => {
        const res = await fetch(`${API}/api/admin/applications/${id}`, { credentials: "include" });
        if (res.ok) {
            setSelected(await res.json());
            setNote("");
        }
    };

    const update = async (id, payload) => {
        const res = await fetch(`${API}/api/admin/applications/${id}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (res.ok) {
            if (payload.status) {
                setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: payload.status } : it)));
                setSelected((prev) => (prev && prev.id === id ? { ...prev, status: payload.status } : prev));
            }
            if (payload.note) {
                setNote("");
                openDetail(id);
            }
        }
    };

    const downloadFile = async (applicationId, stored, original) => {
        const res = await fetch(`${API}/api/admin/applications/${applicationId}/files/${stored}`, { credentials: "include" });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = original || "document";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!user) return <AdminLoading />;
    if (!hasPermission("applications")) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] text-ink">
                <AdminNav active="applications" email={user.email} user={user} onLogout={logout} />
                <AdminAccessDenied section="applications" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory text-ink">
            <AdminNav active="applications" email={user.email} user={user} onLogout={logout} />

            <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <h1 className="font-serif text-3xl font-medium tracking-tight">Applications</h1>
                <p className="mt-1 text-sm text-ink/55">
                    Review candidates, track their progress and download submitted documents.
                </p>

                <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative flex-1">
                        <Search size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/35" />
                        <input
                            data-testid="applications-search-input"
                            type="text"
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            placeholder="Search name, email, profession, job…"
                            className="w-full border border-ink/15 bg-ivory py-3 pl-11 pr-4 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-forest"
                        />
                    </div>
                    <select
                        data-testid="applications-filter-job"
                        value={filters.job_id}
                        onChange={(e) => setFilters((f) => ({ ...f, job_id: e.target.value }))}
                        className="cursor-pointer border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none focus:border-forest"
                    >
                        <option value="">All jobs</option>
                        {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                    </select>
                    <select
                        data-testid="applications-filter-status"
                        value={filters.status}
                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                        className="cursor-pointer border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none focus:border-forest"
                    >
                        <option value="">All statuses</option>
                        {APPLICATION_STATUSES.map((s) => (
                            <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                        ))}
                    </select>
                </div>

                <div className="mt-8" data-testid="applications-list">
                    {loading && items.length === 0 ? (
                        <div className="flex justify-center py-20"><Loader2 size={22} className="animate-spin text-forest" /></div>
                    ) : items.length === 0 ? (
                        <p className="border-t border-ink/10 py-16 text-center text-sm text-ink/50">
                            No applications match these filters yet.
                        </p>
                    ) : (
                        items.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                data-testid={`application-row-${item.id}`}
                                onClick={() => openDetail(item.id)}
                                className="group grid w-full grid-cols-[1fr_auto] items-center gap-x-6 gap-y-1 border-t border-ink/10 px-2 py-5 text-left transition-colors hover:bg-sand sm:grid-cols-[1fr_auto_auto_auto]"
                            >
                                <span className="min-w-0">
                                    <span className="block truncate font-serif text-lg font-medium tracking-tight text-ink">
                                        {item.full_name}
                                    </span>
                                    <span className="block truncate text-xs text-ink/50">
                                        {item.job_title} · {item.profession} · {item.email}
                                    </span>
                                </span>
                                <span className="hidden text-xs text-ink/45 sm:block">{formatDate(item.created_at)}</span>
                                <span className={`hidden border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] sm:block ${STATUS_STYLES[item.status]}`}>
                                    {item.status.replace(/_/g, " ")}
                                </span>
                                <span className="text-forest opacity-0 transition-opacity group-hover:opacity-100">→</span>
                            </button>
                        ))
                    )}
                    <div className="border-t border-ink/10" />
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50" data-testid="application-detail">
                    <div className="absolute inset-0 bg-night/50" onClick={() => setSelected(null)} />
                    <div className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-ivory p-7 sm:p-10">
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-ink/45">
                                    Application · {formatDate(selected.created_at)}
                                    {selected.reference ? ` · Ref ${selected.reference}` : ""}
                                </p>
                                <h2 className="mt-2 font-serif text-2xl font-medium tracking-tight">{selected.full_name}</h2>
                                <p className="mt-1 text-sm text-ink/55">
                                    {selected.job_title} — {selected.job_company}
                                </p>
                                {selected.candidate_id && (
                                    <a
                                        href={`/admin/candidates?open=${selected.candidate_id}`}
                                        data-testid="view-candidate-profile-link"
                                        className="mt-2 inline-block text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:text-champagne"
                                    >
                                        View candidate profile →
                                    </a>
                                )}
                            </div>
                            <button type="button" data-testid="application-detail-close" onClick={() => setSelected(null)} aria-label="Close detail" className="border border-ink/15 p-2 text-ink/60 transition-colors hover:border-forest hover:text-forest">
                                <X size={18} strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="mt-8 border-t border-ink/10">
                            {CANDIDATE_FIELDS.map(([key, label]) =>
                                selected[key] ? (
                                    <div key={key} className="grid grid-cols-[140px_1fr] gap-4 border-b border-ink/10 py-3 sm:grid-cols-[170px_1fr]">
                                        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">{label}</span>
                                        <span className="break-words text-sm text-ink/80">{selected[key]}</span>
                                    </div>
                                ) : null
                            )}
                        </div>

                        {selected.cover_letter && (
                            <div className="mt-6">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">Cover Message</p>
                                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink/75">{selected.cover_letter}</p>
                            </div>
                        )}

                        <div className="mt-6 flex flex-wrap gap-2">
                            {selected.cv?.stored && (
                                <button
                                    type="button"
                                    data-testid="download-cv-btn"
                                    onClick={() => downloadFile(selected.id, selected.cv.stored, selected.cv.original)}
                                    className="inline-flex items-center gap-2 border border-forest/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory"
                                >
                                    <Download size={14} strokeWidth={1.5} />
                                    CV — {selected.cv.original}
                                </button>
                            )}
                            {(selected.documents || []).map((doc) => (
                                <button
                                    key={doc.stored}
                                    type="button"
                                    data-testid={`download-doc-${doc.stored}`}
                                    onClick={() => downloadFile(selected.id, doc.stored, doc.original)}
                                    className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-forest hover:text-forest"
                                >
                                    <Download size={14} strokeWidth={1.5} />
                                    {doc.original}
                                </button>
                            ))}
                        </div>

                        <div className="mt-8">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">Application status</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {APPLICATION_STATUSES.filter((s) => s !== selected.status).map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        data-testid={`app-status-${s}-btn`}
                                        onClick={() => update(selected.id, { status: s })}
                                        className="border border-forest/30 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest transition-colors hover:bg-forest hover:text-ivory"
                                    >
                                        {s.replace(/_/g, " ")}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/45">Internal notes</p>
                            <div className="mt-4 space-y-3">
                                {(selected.notes || []).map((n, i) => (
                                    <div key={i} className="border-l-2 border-champagne/60 bg-sand p-4">
                                        <p className="text-sm leading-relaxed text-ink/75">{n.text}</p>
                                        <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-ink/40">
                                            {n.by} · {formatDate(n.at)}
                                        </p>
                                    </div>
                                ))}
                                {(selected.notes || []).length === 0 && (
                                    <p className="text-sm text-ink/45">No notes yet.</p>
                                )}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <input
                                    data-testid="note-input"
                                    type="text"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Add an internal note…"
                                    className="min-w-0 flex-1 border border-ink/15 bg-ivory px-4 py-3 text-sm outline-none transition-colors placeholder:text-ink/35 focus:border-forest"
                                />
                                <button
                                    type="button"
                                    data-testid="note-add-btn"
                                    onClick={() => note.trim() && update(selected.id, { note })}
                                    className="shrink-0 border border-forest/30 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
